import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight, ShieldCheck, Wrench, Package, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, buildWhatsAppUrl } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import ProductCard from "@/components/ProductCard";
import useSEO from "@/hooks/useSEO";

const BRANDS = {
  airman: {
    name: "Airman",
    brand_key: "Airman",
    tagline: "Portable Air Compressor",
    hero_title: "Jual Kompresor Airman di Indonesia",
    seo_title: "Jual Kompresor Airman PDS Series | Teknik Diesel Indonesia",
    seo_description: "Teknik Diesel Indonesia menyediakan berbagai model portable air compressor Airman PDS Series untuk kebutuhan proyek, workshop, dan industri.",
    intro: "Teknik Diesel Indonesia menyediakan berbagai model portable air compressor Airman PDS Series untuk kebutuhan proyek, workshop, dan industri. Hubungi kami untuk informasi kondisi unit, spesifikasi, harga, dan ketersediaan terbaru.",
    count_label: "Model",
    count_sub: "PDS Series di katalog",
    highlights: [
      { icon: Award, title: "PDS Series", text: "Airman PDS Series merupakan lini portable air compressor untuk aplikasi lapangan." },
      { icon: Package, title: "Beragam Model", text: "Katalog kami mencakup beberapa model PDS. Hubungi kami untuk informasi lengkap." },
      { icon: ShieldCheck, title: "Informasi Unit", text: "Spesifikasi, kondisi, dan harga tiap unit disampaikan sesuai unit yang tersedia." },
      { icon: Wrench, title: "Dukungan Service", text: "Dukungan spare parts dan servis dapat dikonsultasikan dengan tim kami." },
    ],
    applications: ["Proyek konstruksi", "Aplikasi lapangan", "Kebutuhan pneumatic tools", "Aplikasi industri"],
    seo_copy_title: "Mengapa Menghubungi Teknik Diesel Indonesia untuk Airman?",
    seo_copy: "Teknik Diesel Indonesia menyediakan produk Airman untuk kebutuhan portable air compressor proyek di seluruh Indonesia. Setiap unit dapat dikonsultasikan langsung dengan tim kami — mulai dari kondisi, spesifikasi, hingga ketersediaan stok.",
    disclaimer: "Ketersediaan, kondisi, spesifikasi teknis, dan status distributor mengikuti data resmi yang dikonfirmasi oleh Teknik Diesel Indonesia untuk masing-masing unit.",
  },
  takeda: {
    name: "Takeda",
    brand_key: "Takeda",
    tagline: "Bar Bender & Bar Cutter",
    hero_title: "Jual Bar Bender & Bar Cutter Takeda",
    seo_title: "Jual Bar Bender & Bar Cutter Takeda | Teknik Diesel Indonesia",
    seo_description: "Teknik Diesel Indonesia menyediakan pilihan Bar Bender dan Bar Cutter Takeda untuk kebutuhan pembengkokan dan pemotongan besi tulangan.",
    intro: "Teknik Diesel Indonesia menyediakan pilihan Bar Bender dan Bar Cutter Takeda untuk kebutuhan pekerjaan pembengkokan dan pemotongan besi tulangan. Hubungi kami untuk spesifikasi, kondisi, harga, dan ketersediaan setiap model.",
    count_label: "Model",
    count_sub: "3 Bar Bender • 3 Bar Cutter",
    highlights: [
      { icon: Award, title: "Bar Bender", text: "Model Takeda B32, B42, B52 untuk kebutuhan pembengkokan besi tulangan." },
      { icon: Award, title: "Bar Cutter", text: "Model Takeda C32, C42, C52 untuk kebutuhan pemotongan besi tulangan." },
      { icon: Package, title: "Informasi Model", text: "Konsultasi kebutuhan model kepada tim kami untuk pemilihan yang sesuai." },
      { icon: ShieldCheck, title: "Kondisi Unit", text: "Kondisi tiap unit disampaikan sesuai data yang dikonfirmasi." },
    ],
    applications: ["Proyek konstruksi", "Bengkel besi tulangan", "Fabrikasi struktural"],
    seo_copy_title: "Mengapa Menghubungi Teknik Diesel Indonesia untuk Takeda?",
    seo_copy: "Teknik Diesel Indonesia menyediakan mesin Takeda untuk kebutuhan bar bender dan bar cutter proyek konstruksi. Konsultasikan kebutuhan model, kapasitas, dan ketersediaan langsung kepada tim kami.",
    disclaimer: "Kapasitas teknis, kondisi unit, dan status distributor mengikuti data resmi yang dikonfirmasi oleh Teknik Diesel Indonesia untuk masing-masing unit.",
  },
  toyo: {
    name: "Toyo",
    brand_key: "Toyo",
    tagline: "Bar Bender • Bar Cutter • Bar Roller",
    hero_title: "Jual Bar Bender, Bar Cutter & Bar Roller Toyo",
    seo_title: "Jual Bar Bender, Bar Cutter & Bar Roller Toyo | Teknik Diesel Indonesia",
    seo_description: "Teknik Diesel Indonesia menyediakan Bar Bender, Bar Cutter, dan Bar Roller Toyo untuk kebutuhan pembentukan besi tulangan proyek konstruksi.",
    intro: "Teknik Diesel Indonesia menyediakan lini mesin Toyo — Bar Bender, Bar Cutter, dan Bar Roller — untuk kebutuhan pembentukan besi tulangan proyek konstruksi. Hubungi kami untuk spesifikasi, kondisi, dan ketersediaan tiap model.",
    count_label: "Model",
    count_sub: "Bar Bender • Bar Cutter • Bar Roller",
    highlights: [
      { icon: Award, title: "Bar Bender", text: "Model Toyo B32, B42, B52 untuk pembengkokan besi tulangan." },
      { icon: Award, title: "Bar Cutter", text: "Model Toyo C32, C42, C52 untuk pemotongan besi tulangan." },
      { icon: Award, title: "Bar Roller", text: "Toyo Bar Roller untuk pembentukan besi tulangan." },
      { icon: Package, title: "Konsultasi Unit", text: "Konsultasikan kebutuhan proyek Anda untuk pemilihan model yang tepat." },
    ],
    applications: ["Proyek konstruksi", "Fabrikasi tulangan", "Bengkel besi tulangan"],
    seo_copy_title: "Mengapa Menghubungi Teknik Diesel Indonesia untuk Toyo?",
    seo_copy: "Teknik Diesel Indonesia menyediakan mesin Toyo untuk kebutuhan pembentukan besi tulangan proyek konstruksi. Diskusikan kebutuhan model dan ketersediaan bersama tim kami.",
    disclaimer: "Kapasitas teknis, kondisi unit, dan status distributor mengikuti data resmi yang dikonfirmasi oleh Teknik Diesel Indonesia untuk masing-masing unit.",
  },
  akurad: {
    name: "Akurad",
    brand_key: "Akurad",
    tagline: "Produk Industrial",
    hero_title: "Akurad Cairan Anti Api",
    seo_title: "Akurad Cairan Anti Api | Teknik Diesel Indonesia",
    seo_description: "Teknik Diesel Indonesia menyediakan produk Akurad Cairan Anti Api. Hubungi kami untuk informasi produk lebih lanjut.",
    intro: "Teknik Diesel Indonesia menyediakan produk Akurad. Informasi teknis, aplikasi, dan spesifikasi produk disampaikan berdasarkan dokumen resmi dari prinsipal. Hubungi kami untuk informasi selengkapnya.",
    count_label: "Produk",
    count_sub: "Katalog Akurad",
    highlights: [
      { icon: Award, title: "Akurad", text: "Produk Akurad tersedia melalui Teknik Diesel Indonesia." },
      { icon: Package, title: "Informasi Produk", text: "Hubungi kami untuk detail informasi produk terkini." },
      { icon: ShieldCheck, title: "Aplikasi", text: "Informasi aplikasi produk mengikuti dokumen resmi dari prinsipal." },
      { icon: Wrench, title: "Konsultasi", text: "Konsultasikan kebutuhan Anda kepada tim Teknik Diesel Indonesia." },
    ],
    applications: ["Kebutuhan industri", "Kebutuhan proyek"],
    seo_copy_title: "Mengapa Menghubungi Teknik Diesel Indonesia untuk Akurad?",
    seo_copy: "Teknik Diesel Indonesia menyediakan produk Akurad. Hubungi kami untuk mendapatkan informasi produk, aplikasi, serta ketersediaan terbaru.",
    disclaimer: "Informasi teknis, klaim performa, sertifikasi, dan spesifikasi produk Akurad mengikuti dokumen resmi dari prinsipal. Hubungi kami untuk informasi terkini.",
  },
};

export const BRAND_SLUGS = Object.keys(BRANDS);

export default function BrandPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { settings } = useSettings();
  const wa = settings?.whatsapp || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandMeta, setBrandMeta] = useState(null);

  const brand = BRANDS[slug?.toLowerCase()];

  useSEO({
    title: brand?.seo_title || (brand ? `${brand.name} | Teknik Diesel Indonesia` : "Brand tidak ditemukan | Teknik Diesel Indonesia"),
    description: brand?.seo_description,
    path: brand ? `/brand/${slug.toLowerCase()}` : undefined,
    noindex: !brand,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!brand) return;
    setLoading(true);
    api.get("/products", { params: { brand: brand.brand_key, limit: 100 } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
    // Optional: pull admin-managed brand hero image so the series banner can be edited from CMS.
    api.get(`/brands/slug/${slug.toLowerCase()}`).then((r) => setBrandMeta(r.data)).catch(() => setBrandMeta(null));
  }, [slug]);

  if (!brand) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <div className="text-7xl font-display font-black text-[#FF9418] mb-3">404</div>
        <h1 className="font-display font-bold text-[#171717] text-3xl mb-3">Brand tidak ditemukan</h1>
        <p className="text-[#555555] mb-6">Brand yang Anda cari belum tersedia di katalog kami.</p>
        <Button onClick={() => nav("/produk")} className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg">Lihat Semua Produk</Button>
      </div>
    );
  }

  const waMessage = `Halo Teknik Diesel Indonesia, saya melihat halaman brand ${brand.name} di teknikdiesel.com. Saya ingin mendapatkan informasi mengenai produk ${brand.name}. Terima kasih.`;

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <nav className="max-w-7xl mx-auto px-4 pt-6 flex items-center gap-1.5 text-xs text-[#737373] font-mono-tech" data-testid="brand-breadcrumb">
        <Link to="/" className="hover:text-[#FF9418]">Beranda</Link>
        <ChevronRight size={12} />
        <Link to="/produk" className="hover:text-[#FF9418]">Produk</Link>
        <ChevronRight size={12} />
        <span className="text-[#555555]">Brand</span>
        <ChevronRight size={12} />
        <span className="text-[#171717]">{brand.name}</span>
      </nav>

      {/* Hero — pure typography, no misleading imagery */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]" data-testid={`brand-hero-${slug}`}>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FFF4E8] rounded-full opacity-70 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 industrial-stripes opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 py-14 lg:py-20 grid lg:grid-cols-[1fr_320px] gap-10 items-start relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFF4E8] border border-[#FFD3A2] rounded-full mb-4">
              <span className="w-1.5 h-1.5 bg-[#FF9418] rounded-full" />
              <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[#E9840D] font-semibold">{brand.name.toUpperCase()} • {brand.tagline.toUpperCase()}</span>
            </div>
            <h1 className="font-display font-extrabold text-[#171717] text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight mb-4" data-testid="brand-h1">
              {brand.hero_title}
            </h1>
            <p className="text-[#555555] leading-relaxed mb-6 max-w-2xl">{brand.intro}</p>
            <div className="flex flex-wrap gap-3">
              <a href="#produk" onClick={(e) => { e.preventDefault(); document.getElementById("produk")?.scrollIntoView({ behavior: "smooth" }); }}>
                <Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm shadow-md" data-testid="brand-see-products">
                  Lihat Produk {brand.name} <ArrowRight size={14} />
                </Button>
              </a>
              {wa && (
                <a href={buildWhatsAppUrl(wa, waMessage)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-2 border-[#171717] text-[#171717] hover:bg-[#171717] hover:text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm bg-white" data-testid="brand-whatsapp">
                    Tanya via WhatsApp
                  </Button>
                </a>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-6 shadow-sm">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] font-bold mb-2">Katalog {brand.name}</div>
            <div className="flex items-baseline gap-2 mb-3">
              <div className="font-display font-black text-[#171717] text-5xl leading-none">{products.length}</div>
              <div className="font-mono-tech text-xs uppercase tracking-widest text-[#737373]">{brand.count_label}</div>
            </div>
            <div className="text-sm text-[#555555] border-t border-[#E5E5E5] pt-3">{brand.count_sub}</div>
          </div>
        </div>
      </section>

      {/* Compact "Series" strip — banner (admin-managed) + title + dynamic model chips */}
      <section className="border-b border-[#E5E5E5] bg-white" data-testid="brand-series-strip">
        <div className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
          <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] lg:grid-cols-[220px_1fr] gap-4 md:gap-6 items-center">
            <div className="relative w-full aspect-[4/3] md:aspect-[4/3] rounded-lg overflow-hidden border border-[#E5E5E5] bg-[#F8F8F8]">
              {brandMeta?.hero_image ? (
                <img src={brandMeta.hero_image} alt={`${brand.name} ${brand.tagline}`} loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-3" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(255,148,24,0.06) 14px 28px)" }}>
                  <div className="text-center">
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-[#FF9418] font-bold mb-1">{brand.name}</div>
                    <div className="font-display font-black text-[#171717] text-xl leading-tight">{brand.tagline}</div>
                  </div>
                </div>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] font-bold mb-1">{brand.name} • Series</div>
              <div className="font-display font-extrabold text-[#171717] text-xl lg:text-2xl leading-tight mb-1">{brand.tagline}</div>
              <p className="text-sm text-[#555555] leading-snug mb-3 line-clamp-2">{brand.intro}</p>
              {products.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {products.slice(0, 8).map((p) => (
                    <a key={p.id || p.slug} href={`#produk`} onClick={(e) => { e.preventDefault(); document.getElementById(`prod-${p.slug}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                      className="bg-[#F8F8F8] hover:bg-[#FFF4E8] hover:text-[#FF9418] border border-[#E5E5E5] hover:border-[#FFD3A2] px-2.5 py-1 rounded-md font-display font-semibold text-[11px] text-[#171717] transition"
                      data-testid={`series-chip-${p.slug}`}>
                      {p.model || p.product_name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Products — moved up so real product cards appear promptly */}
      <section id="produk" className="bg-[#F8F8F8] border-b border-[#E5E5E5]" data-testid="brand-products-section">
        <div className="max-w-7xl mx-auto px-4 py-10 lg:py-14">
          <div className="flex items-end justify-between mb-6 lg:mb-8 flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-8 h-0.5 bg-[#FF9418]" />
                <span className="font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] font-semibold">Katalog {brand.name}</span>
              </div>
              <h2 className="font-display font-extrabold text-[#171717] text-2xl lg:text-3xl">Produk {brand.name} di Katalog</h2>
            </div>
            <Link to={`/produk?brand=${encodeURIComponent(brand.brand_key)}`} className="text-sm text-[#555555] hover:text-[#FF9418] font-semibold flex items-center gap-1">
              Filter di Katalog <ArrowRight size={14} />
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-16 text-[#737373]">Memuat produk...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-[#E5E5E5] rounded-xl">
              <div className="font-display font-bold text-[#171717] text-xl mb-2">Belum ada produk {brand.name} di katalog</div>
              <p className="text-[#555555]">Hubungi kami untuk konsultasi ketersediaan.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {products.map((p) => (
                <div key={p.id} id={`prod-${p.slug}`}><ProductCard product={p} /></div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Applications + WhatsApp CTA — moved BELOW products to keep intro compact */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-0.5 bg-[#FF9418]" />
              <span className="font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] font-semibold">Aplikasi</span>
            </div>
            <h2 className="font-display font-extrabold text-[#171717] text-2xl lg:text-3xl mb-4">Aplikasi & Penggunaan</h2>
            <p className="text-[#555555] leading-relaxed mb-4">
              Aplikasi umum untuk produk {brand.name} yang dapat dikonsultasikan dengan Teknik Diesel Indonesia:
            </p>
            <ul className="space-y-2">
              {brand.applications.map((a) => (
                <li key={a} className="flex items-start gap-3 text-[#171717]">
                  <span className="w-1.5 h-1.5 bg-[#FF9418] rounded-full mt-2 shrink-0" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#FFF7EE] border border-[#FFD3A2] rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <span className="w-8 h-0.5 bg-[#FF9418]" />
              <span className="font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] font-semibold">Butuh Bantuan?</span>
            </div>
            <h3 className="font-display font-extrabold text-[#171717] text-2xl mb-3">Diskusi Kebutuhan {brand.name} Anda</h3>
            <p className="text-[#555555] leading-relaxed mb-5">
              Tim Teknik Diesel Indonesia siap membantu Anda memilih produk {brand.name} yang paling sesuai kebutuhan proyek. Hubungi kami untuk penawaran dan informasi terbaru.
            </p>
            {wa ? (
              <a href={buildWhatsAppUrl(wa, waMessage)} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm shadow" data-testid="brand-consult-whatsapp">
                  Chat via WhatsApp
                </Button>
              </a>
            ) : (
              <Link to="/hubungi-kami">
                <Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm">Hubungi Kami</Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* SEO copy + disclaimer */}
      <section className="max-w-4xl mx-auto px-4 py-14">
        <div className="prose prose-neutral max-w-none">
          <h2 className="font-display font-extrabold text-[#171717] text-2xl mb-4">{brand.seo_copy_title}</h2>
          <p className="text-[#555555] leading-relaxed mb-3">{brand.seo_copy}</p>
          <div className="mt-6 p-4 bg-[#F8F8F8] border-l-4 border-[#FF9418] rounded">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-[#737373] mb-1">Catatan</div>
            <p className="text-sm text-[#555555] m-0">{brand.disclaimer}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
